import { Button, Modal } from '@components/common';
import { ChevronLeft, ChevronRight, Info } from 'lucide-react';
import { useMemo, useState } from 'react';

type AccountingHelpPage =
  | 'chart-of-accounts'
  | 'journal-entries'
  | 'accounting-reports'
  | 'posting-rules'
  | 'accounting-periods'
  | 'tax-rates'
  | 'payment-mappings'
  | 'tax-refunds'
  | 'accounts-payable';

interface AccountingHelpContent {
  title: string;
  summary: string;
  whatToDo: string[];
  connections: string[];
}

const ACCOUNTING_HELP_CONTENT: Record<
  AccountingHelpPage,
  AccountingHelpContent
> = {
  'chart-of-accounts': {
    title: 'Chart of Accounts',
    summary:
      'This page defines the ledger structure your entire accounting module relies on. Every automated posting, manual journal, tax setup, and report depends on these accounts being correct.',
    whatToDo: [
      'Review the seeded accounts and confirm they match how the business wants to track cash, receivables, revenue, costs, and liabilities.',
      'Add missing accounts only when the business truly needs a separate balance or report line.',
      'Keep account codes and names stable so mappings and reports remain easy to understand.',
    ],
    connections: [
      'Payment mappings send settlement methods like cash, card, and mobile money into accounts from this page.',
      'Posting rules choose which debit and credit accounts are used when business events happen.',
      'Reports, dashboards, and journals all summarize activity against these accounts.',
    ],
  },
  'journal-entries': {
    title: 'Journal Entries',
    summary:
      'This is the controlled workspace for manual accounting adjustments. Use it when something should hit the ledger but was not created automatically by POS, accommodation, or OTA workflows.',
    whatToDo: [
      'Create drafts first, add balanced lines, then post only after you are satisfied with the entry.',
      'Use references and memos so future users can trace why the adjustment happened.',
      'Reverse posted entries instead of editing history directly.',
    ],
    connections: [
      'Manual journals flow into the same ledger, dashboard, and reports as automated entries.',
      'Accounting periods must be open before a draft can be posted.',
      'The chart of accounts controls which accounts can be used on each line.',
    ],
  },
  'accounting-reports': {
    title: 'Accounting Reports',
    summary:
      'This page turns your ledger into decision-ready views. Use it to validate balances, drill into activity, and investigate what was posted by each module.',
    whatToDo: [
      'Use Trial Balance to confirm account balances are reasonable.',
      'Use General Ledger when you need line-level detail for a balance or posting source.',
      'Use Account Activity to understand movement over time by account and module.',
    ],
    connections: [
      'Every report is built from journal entries created manually or by system triggers.',
      'Filters help you isolate a company-wide view or a specific outlet and date range.',
      'These reports are the source of truth behind the accounting dashboard metrics.',
    ],
  },
  'posting-rules': {
    title: 'Posting Rules',
    summary:
      'Posting rules tell the system which accounts to debit and credit when specific business events happen. They are the bridge between operations and accounting automation.',
    whatToDo: [
      'Start with the most common scenarios such as settlements, refunds, OTA payouts, and adjustments.',
      'Use guided event, reference, and metadata options so rule naming stays consistent.',
      'Review priorities carefully when more than one rule could apply to a similar event.',
    ],
    connections: [
      'These rules work with payment mappings, tax rates, and the chart of accounts.',
      'When a POS or accommodation event is posted, the engine reads these rules to build journals.',
      'If this page is incomplete, the dashboard may show setup blockers and automation gaps.',
    ],
  },
  'accounting-periods': {
    title: 'Accounting Periods',
    summary:
      'Accounting periods control when entries are allowed to be posted. They protect reporting accuracy by separating open operational work from closed financial history.',
    whatToDo: [
      'Create periods early and keep the current period open while operations are still posting activity.',
      'Close a period only after reviews, reconciliations, and corrections are complete.',
      'Use clear names so everyone can recognize month, quarter, or custom periods quickly.',
    ],
    connections: [
      'Manual journals and automated postings both depend on an open period.',
      'Reports and dashboard trends make more sense when periods are maintained consistently.',
      'Closing periods helps prevent accidental back-posting into finalized history.',
    ],
  },
  'tax-rates': {
    title: 'Tax Rates',
    summary:
      'Tax rates define how sales and purchase tax should be recognized and where those balances should land in the ledger.',
    whatToDo: [
      'Create the active rates your business actually uses and confirm the percentage matches how the system captures tax.',
      'Map output and input accounts so tax journals can separate tax from revenue or recoverable amounts.',
      'Deactivate unused rates instead of deleting them when they should no longer be applied.',
    ],
    connections: [
      'POS and accommodation postings rely on tax configuration to split revenue correctly.',
      'Posting rules can further narrow behavior using tax-aware conditions.',
      'Reports and the dashboard depend on these mappings to avoid overstating revenue.',
    ],
  },
  'payment-mappings': {
    title: 'Payment Mappings',
    summary:
      'This page connects operational payment methods like cash, card, mobile money, and credit to the ledger accounts that should receive those balances.',
    whatToDo: [
      'Map every payment method that should create accounting entries.',
      'Use receivable accounts for methods like CREDIT and liquid accounts for cash and bank-like methods.',
      'Review mappings whenever a new payment channel is introduced in operations.',
    ],
    connections: [
      'Settlement triggers use these mappings to decide the debit side of journals.',
      'Without mappings, the system cannot confidently post cash, bank, or receivable activity.',
      'The dashboard setup panel treats missing mappings as a blocker.',
    ],
  },
  'tax-refunds': {
    title: 'Tax Refunds',
    summary:
      'Tax refund configuration helps the business decide how refund scenarios should unwind revenue, tax, and related balances without creating inconsistent journal logic.',
    whatToDo: [
      'Review the supported refund scenarios and create rules for the ones your business uses.',
      'Make sure refund rules match how the original sale or booking was posted.',
      'Use scenario-specific metadata to keep refund automation predictable.',
    ],
    connections: [
      'Refund rules extend posting rules rather than replacing them.',
      'Correct tax-rate and account mappings are required for refund journals to land correctly.',
      'The dashboard will only be reliable if both original sales and refunds are posted consistently.',
    ],
  },
  'accounts-payable': {
    title: 'Accounts Payable',
    summary:
      'This area is reserved for supplier-facing accounting workflows such as payable balances, invoice tracking, and settlement scheduling.',
    whatToDo: [
      'Use this space later for supplier bills, aging, approvals, and payment tracking.',
      'Keep supplier expense and liability accounts ready in the chart of accounts ahead of payable automation.',
    ],
    connections: [
      'Payables will eventually connect inventory purchasing, expense recognition, and cash movement.',
      'Reports and dashboard cash metrics will surface supplier obligations once this module is active.',
    ],
  },
};

const ONBOARDING_STEPS = [
  {
    title: 'Confirm the chart of accounts',
    body: 'Start by reviewing the seeded accounts so the ledger matches how the business thinks about cash, receivables, taxes, room revenue, sales, and expenses.',
  },
  {
    title: 'Set tax rates and tax accounts',
    body: 'Create the active tax rates your outlets actually use, then map the output and input accounts so tax is separated cleanly from revenue and recoverables.',
  },
  {
    title: 'Map payment methods',
    body: 'Tell the system where cash, bank, mobile money, card, NC, and credit activity should land. This is what lets operational settlements become accounting entries.',
  },
  {
    title: 'Configure posting rules',
    body: 'Define how sales, accommodation, OTA, refund, and adjustment events should debit and credit the ledger. This is the automation brain of the module.',
  },
  {
    title: 'Open an accounting period',
    body: 'Create and keep the current period open so both manual and automated journals can post. Close periods only after review and reconciliation.',
  },
  {
    title: 'Review journals and reports',
    body: 'Use journal entries and accounting reports to validate that real business events are landing exactly where you expect before relying on dashboard insights.',
  },
];

interface AccountingPageInfoButtonProps {
  page: AccountingHelpPage;
}

export function AccountingPageInfoButton({
  page,
}: AccountingPageInfoButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const content = ACCOUNTING_HELP_CONTENT[page];

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setIsOpen(true)}
        aria-label={`Learn about ${content.title}`}
        className="min-w-0 px-2.5"
      >
        <Info size={15} />
      </Button>

      <Modal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title={content.title}
        size="lg"
      >
        <div className="space-y-4">
          <div className="rounded-2xl bg-pos-primary/8 px-4 py-3 text-sm text-gray-700">
            {content.summary}
          </div>

          <div>
            <h3 className="text-sm font-semibold text-black">
              What to do here
            </h3>
            <div className="mt-2 space-y-2">
              {content.whatToDo.map((item) => (
                <div
                  key={item}
                  className="rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-700"
                >
                  {item}
                </div>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-black">
              How this connects
            </h3>
            <div className="mt-2 space-y-2">
              {content.connections.map((item) => (
                <div
                  key={item}
                  className="rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-700"
                >
                  {item}
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end border-t border-gray-100 pt-3">
            <Button onClick={() => setIsOpen(false)}>Close</Button>
          </div>
        </div>
      </Modal>
    </>
  );
}

interface AccountingOnboardingModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function AccountingOnboardingModal({
  isOpen,
  onClose,
}: AccountingOnboardingModalProps) {
  const [stepIndex, setStepIndex] = useState(0);

  const step = useMemo(() => ONBOARDING_STEPS[stepIndex], [stepIndex]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Accounting setup guide"
      size="lg"
    >
      <div className="space-y-4">
        <div className="flex items-center justify-between rounded-2xl bg-pos-primary/8 px-4 py-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-pos-primary">
              Step {stepIndex + 1} of {ONBOARDING_STEPS.length}
            </p>
            <h3 className="mt-1 text-lg font-semibold text-black">
              {step.title}
            </h3>
          </div>
          <div className="flex gap-1">
            {ONBOARDING_STEPS.map((item, index) => (
              <span
                key={item.title}
                className={[
                  'h-2.5 w-2.5 rounded-full',
                  index === stepIndex ? 'bg-pos-primary' : 'bg-pos-primary/20',
                ].join(' ')}
              />
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-gray-200 px-4 py-4 text-sm leading-6 text-gray-700">
          {step.body}
        </div>

        <div className="rounded-2xl border border-gray-200 px-4 py-3">
          <p className="text-sm font-semibold text-black">
            Recommended setup order
          </p>
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            {ONBOARDING_STEPS.map((item, index) => (
              <div
                key={item.title}
                className={[
                  'rounded-xl px-3 py-2 text-sm',
                  index === stepIndex
                    ? 'bg-pos-primary text-white'
                    : 'bg-gray-50 text-gray-600',
                ].join(' ')}
              >
                {index + 1}. {item.title}
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-between border-t border-gray-100 pt-3">
          <Button
            variant="outline"
            onClick={() => setStepIndex((current) => Math.max(current - 1, 0))}
            disabled={stepIndex === 0}
          >
            <ChevronLeft size={16} />
            Previous
          </Button>

          {stepIndex === ONBOARDING_STEPS.length - 1 ? (
            <Button
              onClick={() => {
                setStepIndex(0);
                onClose();
              }}
            >
              Finish
            </Button>
          ) : (
            <Button
              onClick={() =>
                setStepIndex((current) =>
                  Math.min(current + 1, ONBOARDING_STEPS.length - 1),
                )
              }
            >
              Next
              <ChevronRight size={16} />
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}
